import {
  Box,
  Step,
  StepLabel,
  Stepper,
  StepConnector,
  styled,
  useTheme,
} from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { generateRoute } from "ui";
import { AdRoutes } from "@/constants/ad-routes";
import { CheckOutlined } from "@mui/icons-material";

const TEMP_ROUTE_WORKFLOW_STEPS = ["Create Sequence", "Create Route"];

// Sibling of RegionWorkflowStepper, same visual treatment but navigates the
// create-temp-route routes instead - kept separate rather than parameterized
// so the two flows' navigation logic doesn't get entangled.
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  "&.MuiStepConnector-root": {
    top: "50%",
    left: "calc(-50% + 16px)",
    right: "calc(50% - 16px)",
    maxWidth: "27px",
  },
  "& .MuiStepConnector-line": {
    borderColor: theme.palette.grey[200],
    borderTopWidth: 1,
    borderRadius: 1,
  },
}));

const CustomStepIcon = styled("div")<{
  stepIndex: number;
  activeStep: number;
}>(({ theme, stepIndex, activeStep }) => {
  const isCompleted = stepIndex < activeStep;
  const isActive = stepIndex === activeStep;

  return {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    backgroundColor: isCompleted ? "#005140" : theme.palette.grey[600],
    color: isCompleted
      ? "#7EF8D4"
      : isActive
        ? "#FFFFFF"
        : theme.palette.grey[200],
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
  };
});

interface CreateTempRouteWorkflowStepperProps {
  className?: string;
}

const CreateTempRouteWorkflowStepper: React.FC<
  CreateTempRouteWorkflowStepperProps
> = ({ className }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const { date } = useParams<{ date: string }>();

  const isSequencePage = location.pathname.includes(
    "/create-temp-route/sequence",
  );
  const activeStep = isSequencePage ? 0 : 1;

  const handleStepClick = (stepIndex: number) => {
    if (!date) return;

    if (stepIndex === 0 && !isSequencePage) {
      navigate(generateRoute(AdRoutes.createTempRouteSequence, { date }));
    } else if (stepIndex === 1 && isSequencePage) {
      navigate(generateRoute(AdRoutes.createTempRouteDetails, { date }));
    }
  };

  return (
    <Box className={className} sx={{ maxWidth: "300px" }}>
      <Stepper
        activeStep={activeStep}
        connector={<CustomConnector />}
        style={{ marginTop: "15px", marginBottom: "20px" }}
      >
        {TEMP_ROUTE_WORKFLOW_STEPS.map((label, index) => {
          const isCompleted = index < activeStep;
          const isActive = index === activeStep;

          return (
            <Step key={label}>
              <StepLabel
                sx={{
                  cursor: "pointer",
                  "& .MuiStepLabel-label": {
                    color: isActive ? "#FFFFFF" : theme.palette.grey[200],
                    fontWeight: 500,
                  },
                }}
                onClick={() => handleStepClick(index)}
                StepIconComponent={() => (
                  <CustomStepIcon
                    stepIndex={index}
                    activeStep={activeStep}
                    onClick={() => handleStepClick(index)}
                  >
                    {isCompleted ? (
                      <CheckOutlined sx={{ width: "14px", height: "14px" }} />
                    ) : (
                      index + 1
                    )}
                  </CustomStepIcon>
                )}
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default CreateTempRouteWorkflowStepper;
